//import { LineItem } from "../interfaces/LineItem";
import { toUsableCrewSize } from "../../datasources/laborCrews";

/**
 * Naming conventions for ranges and tables in the workbook:
 * - `__` prefix: Used to locate one or more rows of data.
 * - `\` prefix: Used to locate a column of this row data.
 * - `_` prefix: Used to locate a single value for a specific cell.
 */

export const rowNames = {
  vmSummary: "__vmSummary",
  startOfPrint: "__print", //deprecated
  legacyReportRange: "__report", //deprecated
  lineItems: "__lineItems",
  markups: "__markups",
  estimateSOV: "__estimateSOV",
  alternatesSOV: "__alternatesSOV",
  alternateMarkups: "__alternateMarkups",
  contractingSOV: "__contractingSOV",
  contractingMarkups: "__contractingMarkups",
  alternateItems: "__alternateItems",
  gcgrStaffItems: "__gcgrStaffItems",
  gcgrLineItems: "__gcgrLineItems",
  costDenominators: "__costDenominators",
  subTotals: "__subtotals",
  grandTotal: "__grandTotal",
  floorAreaItems: "__floorAreaItems",
  unitMixItems: "__unitMixItems",
  roomItems: "__roomItems",
};

// these must be prefixed with a backslash in the worksheet
export const columnNames = {
  id: "id",
  title: "title",
  type: "type",
  vmCategory: "vm_category",
  status: "status",
  totalCost: "total_cost",
  notes: "notes",
  costPerGSF: "cost_per_gsf",
  useGroups: "use_groups",
  floorGroups: "floor_groups",
  /** The Cross Tab SOV's breakout block; the sheet's Cross Tab Columns option picks what fills it. */
  crossTab: "cross_tab",
  unitTypes: "unit_types",
  markupPercentages: "markup_percent",
  budgetPerGSF: "budget_per_gsf",
  budgetDeltaPerGSF: "budget_delta_per_gsf",
  budgetDelta: "budget_delta",
  holds: "hold",
  allowances: "allowance",
  tradeCleanup: "trade_cleanup",
  estimator: "estimator",
  lastUpdated: "last_updated",
  updatedBy: "updated_by",
  code: "code",
  total: "total",
  budget: "budget",
  alternates: "alternates",
  quantity: "quantity",
  bids: "bids",
  buyStats: "buy_stats",
  scopeAlternates: "scope_alternates",
};

export const rangeNames = {
  reportGrouping: "_reportGrouping",
  gsf: "_gsf",
  date: "_date",
  title: "_title",
};

/**
 * Columns computed from a line item at print time, for a `\` range name the item has no field for.
 * The crew columns read `crew_size`, which only estimate line items carry (`assignLaborCrewFields`);
 * anything else, like a markup or a GCGR row, counts as one worker. Their math and headers follow
 * the app's (`common/utils/productionRates.ts`, `common/utils/productionRateHeaders.ts`).
 */
export const calculatedRangeNames: Record<string, (lineItem: Record<string, any>) => number | null> = {
  total_labor: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const labor_uc = lineItem?.labor_uc || 0;
    const labor_total = quantity * labor_uc;
    return labor_total;
  },
  total_material: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const material_uc = lineItem?.material_uc || 0;
    const material_total = quantity * material_uc;
    return material_total;
  },
  total_sub: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const sub_uc = lineItem?.sub_uc || 0;
    const sub_total = quantity * sub_uc;
    return sub_total;
  },
  total_equip: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const equipment_uc = lineItem?.equip_uc || 0;
    const equip_total = quantity * equipment_uc;
    return equip_total;
  },
  total_other: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const other_uc = lineItem?.other_uc || 0;
    const other_total = quantity * other_uc;
    return other_total;
  },
  labor_hrs: (lineItem: Record<string, any>): number => {
    const quantity = lineItem?.quantity || 0;
    const prod_rate = lineItem?.prod_rate || 0;
    const labor_hours = quantity * prod_rate;
    return labor_hours;
  },
  hrs_per_uom: (lineItem: Record<string, any>): number => {
    const prod_rate = lineItem?.prod_rate || 0;
    return prod_rate;
  },
  uom_per_hr: (lineItem: Record<string, any>): number | null => {
    const prod_rate = lineItem?.prod_rate || 0;
    return prod_rate === 0 ? null : 1 / prod_rate;
  },
  // Blank, as in the app, when the crew has no workers: there are no individuals to share the hours.
  individual_hrs: (lineItem: Record<string, any>): number | null => {
    const crewSize = toUsableCrewSize(lineItem?.crew_size);
    if (crewSize <= 0) return null;
    const quantity = lineItem?.quantity || 0;
    const prod_rate = lineItem?.prod_rate || 0;
    return quantity * prod_rate * crewSize;
  },
  ind_hrs_per_uom: (lineItem: Record<string, any>): number => {
    const prod_rate = lineItem?.prod_rate || 0;
    return prod_rate * toUsableCrewSize(lineItem?.crew_size);
  },
  uom_per_ind_hr: (lineItem: Record<string, any>): number | null => {
    const prod_rate = lineItem?.prod_rate || 0;
    const individualProdRate = prod_rate * toUsableCrewSize(lineItem?.crew_size);
    return individualProdRate === 0 ? null : 1 / individualProdRate;
  },
  man_weeks: (lineItem: Record<string, any>): number => {
    const manWeeks = ((lineItem?.allocation || 0) / 100) * (lineItem?.duration || 0);
    return manWeeks;
  },
  man_hours: (lineItem: Record<string, any>): number => {
    const manHours = ((lineItem?.allocation || 0) / 100) * (lineItem?.duration || 0) * 40;
    return manHours;
  },
};

/**
 * Calculated columns that roll up by summation, so a group row can show the total of the rows
 * beneath it. The remaining `calculatedRangeNames` entries are rates and must not be summed.
 */
export const summableCalculatedColumnNames = [
  "labor_hrs",
  "individual_hrs",
  "total_labor",
  "total_material",
  "total_sub",
  "total_equip",
  "total_other",
] as const;
